import { Modal, Button, SimpleGrid, Select, Group, Stack, NumberInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { StableKey } from 'app/models/common-types';
import { paidToOptions } from 'app/models/dropdown-options';
import { addSubTradedFacilityFee, editSubTradedFacilityFee } from 'app/state/ducks/traded-facilities/thunks';
import TextInput from 'app/views/components/inputs/TextInput'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'


interface Props {
    fee: any | null;
    opened: boolean;
    close: () => void;
    facilityId: StableKey
    subTradeId: StableKey
}

function SubTradeFacilityFeeModal({ opened, close, fee, subTradeId, facilityId }: Props) {
    const dispatch = useDispatch();
    const form = useForm({
        initialValues: {
            feeType: fee?.type ?? '',
            overrideAmount: Number(fee?.overrideAmount ?? ''),
            paidTo: fee?.paidTo ?? '',
        },

        validate: {
            feeType: (value) =>
                value.trim().length === 0 ? 'Type is required' : null,
            overrideAmount: (value) =>
                !value ? 'Amount is required' : null,
            paidTo: (value) =>
                value.trim().length === 0 ? 'Paid To is required' : null,
        },
        validateInputOnBlur: true,
        validateInputOnChange: true,
    });

    useEffect(() => {
        form.setValues({
            feeType: fee?.type ?? '',
            overrideAmount: Number(fee?.overrideAmount ?? ''),
            paidTo: fee?.paidTo ?? '',
        })
    }, [opened, fee])

    // Submit handler
    const handleSubmit = async (values: typeof form.values) => {
        let result: any
        if (fee) {
            result = await dispatch(editSubTradedFacilityFee({
                subTradeKey: subTradeId,
                facilityKey: facilityId,
                oldFeeType: fee.type,
                newFee: {
                    ...values,
                    reason: fee.reason,
                }
            }))
        }
        else {
            result = await dispatch(addSubTradedFacilityFee({
                subTradeKey: subTradeId,
                facilityKey: facilityId,
                newFee: {
                    ...values,
                    reason: null,
                }
            }))
        }
        if (result && result.success) {
            SuccessNotification({ title: 'Fee added successfully', message: 'Fee added successfully' })
        }
        else {
            ErrorNotification({ title: 'Failed to add fee', message: result?.payload ?? 'Failed to add fee' })
        }

        // Close the modal upon successful submission
        close();
    };

    return (
        <Modal
            opened={opened}
            onClose={close}
            centered
            withCloseButton={false}
            size="70%"
            className="modal-body create-new-form fit-content-modal"

        >
            <Stack w="100% !important" >
                <div className="add-modal-fee" style={{ width: '100%' }}>
                    <div className="add-modal-fee-title-header">
                        <div className="add-modal-fee-title">
                            <div className="add-modal-fee-title-frame">
                                <div className="add-modal-fee-title-text">Add Fee</div>
                            </div>
                        </div>
                        <div className="add-modal-fee-header-actions">
                            <div className="add-modal-fee-title-actions-expand">
                                <Button
                                    onClick={close}
                                    variant="subtle"
                                    style={{
                                        padding: 0,
                                        background: 'transparent',
                                        height: 30,
                                        width: 30,
                                        minWidth: 'auto',
                                    }}
                                    aria-label="Close modal"
                                >
                                    <CloseIcon style={{ width: '30px' }} />
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="content" style={{ width: '100%' }}>
                        <form onSubmit={form.onSubmit(handleSubmit)}>
                            <div className="create-new">
                                <div>
                                    <TextInput
                                        label="Type"
                                        placeholder="Enter Type"
                                        {...form.getInputProps('feeType')}
                                    />
                                </div>

                                <SimpleGrid cols={2} spacing="md"
                                >
                                    <NumberInput
                                        label="Amount"
                                        precision={2}
                                        placeholder="Enter Amount"
                                        parser={value =>
                                            value ? value.replace(/\$\s?|(,*)/g, '') : ''
                                        }
                                        formatter={value =>
                                            !Number.isNaN(parseFloat(value ?? ''))
                                                ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                : ''
                                        }
                                        {...form.getInputProps('overrideAmount')}
                                    />

                                    {/* Paid To */}
                                    <Select
                                        label="Paid To"
                                        placeholder="Enter Recipient"
                                        data={paidToOptions}
                                        {...form.getInputProps('paidTo')}
                                    />
                                </SimpleGrid>
                                {/* Action buttons */}
                                <Group position="right" mt="md">
                                    <Button className='add-modal-fee-cancel-button' onClick={close}>
                                        <div className="add-modal-fee-cancel-button-text"> Cancel </div>
                                    </Button>
                                    <Button disabled={!form.isValid()} className='add-modal-fee-save-button' type="submit"><div className='add-modal-fee-save-button-text'>Save</div></Button>
                                </Group>
                            </div>
                        </form>
                    </div>
                </div>

            </Stack>
        </Modal >
    );
}

export default SubTradeFacilityFeeModal;